import { PrismicRichText } from '@prismicio/react';
import IconArrowDown from '@remote-com/norma/icons/arrow-down.svg';
import { useRef, useContext } from 'react';
import { Button } from 'remote-com-shared/components/Button';
import HeaderContext from 'remote-com-shared/components/Header/context';
import { Hero as HeroComponent } from 'remote-com-shared/components/Hero';
import HeroContext from 'remote-com-shared/components/Hero/context';
import { Title, Description, ContentLayout } from 'remote-com-shared/components/Hero/styles';
import { fluidProp, whenProp } from 'remote-com-shared/style/helpers';
import styled, { css } from 'styled-components';

import BgImage from '../assets/hero-bg.png';

import { CustomCTAHeader } from '@/src/components/Header';
import { transformProperties } from '@/src/domains/prismic/helpers';
import { ScrollButton } from '@/src/views/G2LeaderPage/HeroSlice';
import { SimpleFormModalButton } from '@/src/views/Referrals/SimpleForm';

const Hero = styled(HeroComponent)`
  font-family: var(--typography-fontBrand);
  background-image: url(${BgImage.src});
  background-size: cover;
  background-color: #e03d5c;

  ${Title} {
    font-family: var(--typography-fontBossa);
    ${fluidProp('font-size', '34px', '60px')}
  }

  ${ContentLayout} {
    max-width: 650px;
  }

  ${Description} {
    color: var(--colors-blank);
    ${({ theme }) => theme.typography.titleXL};
    font-family: var(--typography-fontBossa);
    max-width: unset;
    margin-top: 32px;
  }

  ${ScrollButton} {
    margin-top: 100px;
  }
`;

const CTAButton = styled(Button)`
  && {
    background: var(--colors-blank);
    color: #d3433c;
    ${({ theme }) => theme.typography.body};
    box-shadow: 0px 3px 11px rgba(0, 0, 0, 0.25);
  }
`;

export const HeaderCTAButton = styled(CTAButton)`
  && {
    color: var(--colors-darkBlue);
    font-size: 12px;
    font-weight: 500;
    min-width: 186px;

    ${whenProp(
      ['$isFixed', '$isOpen'],
      css`
        background-color: var(--colors-irisBlue);
        color: var(--colors-blank);
        box-shadow: 0px 6px 12px rgba(98, 77, 227, 0.3);
      `
    )}
  }
`;

const CTAWrapper = styled.div`
  padding-top: 50px;
`;

export function HeroSlice({ slice }) {
  const { primary, context } = transformProperties(slice);
  const { title, description, ctaLabel, headerCtaLink, headerCtaLabel } = primary;
  const headerRef = useRef(null);
  const { isIntersecting } = useContext(HeroContext);

  const {
    formId,
    gatedFile,
    modalFormTitle,
    modalFormDescription,
    modalFormSubmitLabel,
    downloadButtonLabel,
    eventName,
  } = context;

  return (
    <>
      <HeaderContext.Provider value={headerRef}>
        <CustomCTAHeader
          cta={
            headerCtaLink ? (
              <HeaderCTAButton
                size="lg"
                href={headerCtaLink?.url}
                target={headerCtaLink?.target}
                rel="nofollow"
                $isFixed={!isIntersecting}
              >
                {headerCtaLabel || 'Learn more'}
              </HeaderCTAButton>
            ) : (
              <SimpleFormModalButton
                modalFormId={formId}
                modalFormSubmitLabel={modalFormSubmitLabel}
                modalFormDescription={modalFormDescription}
                modalFormTitle={modalFormTitle}
                gatedFile={gatedFile}
                gatedFileDownloadLabel={downloadButtonLabel}
                eventName={eventName}
                button={{ as: HeaderCTAButton, $isFixed: !isIntersecting }}
              >
                {headerCtaLabel}
              </SimpleFormModalButton>
            )
          }
        />

        <Hero
          title={<PrismicRichText field={title} />}
          description={<PrismicRichText field={description} />}
          actions={
            ctaLabel && (
              <CTAWrapper>
                <SimpleFormModalButton
                  modalFormId={formId}
                  modalFormSubmitLabel={modalFormSubmitLabel}
                  modalFormDescription={modalFormDescription}
                  modalFormTitle={modalFormTitle}
                  gatedFile={gatedFile}
                  gatedFileDownloadLabel={downloadButtonLabel}
                  eventName={eventName}
                  button={{ as: CTAButton }}
                >
                  {ctaLabel}
                </SimpleFormModalButton>
                <ScrollButton href="#benefits">
                  <IconArrowDown />
                </ScrollButton>
              </CTAWrapper>
            )
          }
          preview="0vh"
        />
      </HeaderContext.Provider>
    </>
  );
}
